import { firstNameFromUserKey } from "../ratings";
import type { DebriefCard, DebriefEvent, DebriefFollowup, DebriefNote, LeadRating } from "./types";

export const DEBRIEF_EVENTS: DebriefEvent[] = [
  {
    eventKey: "nbaa-bace-2026",
    label: "NBAA-BACE 2026",
    shortLabel: "NBAA-BACE 26",
    attribution: "NBAA BACE 2026",
    campaignKey: "nbaa-bace-2026",
  },
];

export const PRIORITIES: { id: string; label: string }[] = [
  { id: "scale-with-credits", label: "Scale with Credits" },
  { id: "strategic-mro", label: "Strategic MRO" },
  { id: "faa-trust", label: "FAA Trust" },
  { id: "oem-knowledge", label: "OEM Knowledge" },
  { id: "talent-attraction", label: "Talent Attraction" },
  { id: "capital-raise", label: "Capital Raise" },
];

/** Earlier catalog names that still appear on stored accounts. */
const PRIORITY_ALIASES: Record<string, string> = {
  "Talent Development": "talent-attraction",
  "talent-development": "talent-attraction",
};

/** Map a stored label or id onto the current catalog, or null when it is not a known priority. */
export function priorityIdForStoredValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const aliased = PRIORITY_ALIASES[trimmed];
  if (aliased) return aliased;
  return PRIORITIES.find((priority) => priority.label === trimmed || priority.id === trimmed)?.id ?? null;
}

/** Current catalog label for a stored priority. Unknown text is returned unchanged. */
export function displayPriorityLabel(value: string): string {
  const id = priorityIdForStoredValue(value);
  return PRIORITIES.find((priority) => priority.id === id)?.label ?? value.trim();
}

/** Catalog ids for a stored strategic_priorities array, in catalog order. */
export function priorityIdsFromStored(value: unknown): string[] {
  const matched = new Set<string>();
  const consider = (raw: unknown) => {
    if (typeof raw !== "string") return;
    const id = priorityIdForStoredValue(raw);
    if (id) matched.add(id);
  };
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === "string") consider(item);
      else if (item && typeof item === "object") {
        const row = item as Record<string, unknown>;
        consider(row.label);
        consider(row.id);
      }
    }
  }
  return PRIORITIES.filter((priority) => matched.has(priority.id)).map((priority) => priority.id);
}

const LINKEDIN_HOST = /^(?:www\.)?linkedin\.com$/i;

export function eventByKey(eventKey: string): DebriefEvent {
  return DEBRIEF_EVENTS.find((event) => event.eventKey === eventKey) ?? DEBRIEF_EVENTS[0];
}

export function compareEventContact(a: DebriefCard, b: DebriefCard): number {
  if (a.eventContactId == null && b.eventContactId == null) return a.fullName.localeCompare(b.fullName);
  if (a.eventContactId == null) return 1;
  if (b.eventContactId == null) return -1;
  if (a.eventContactId !== b.eventContactId) return a.eventContactId - b.eventContactId;
  return a.fullName.localeCompare(b.fullName);
}

/** Server display when Intel sent one. Otherwise the NBAA show form of a real integer. Null stays blank. */
export function contactIdLabel(card: DebriefCard): string | null {
  const display = card.eventContactDisplay?.trim();
  if (display) return display;
  if (card.eventContactId == null) return null;
  const campaign = card.campaignKey ?? card.eventKey;
  if (campaign === "nbaa-bace-2026") return `NBAA26-${String(card.eventContactId).padStart(6, "0")}`;
  return String(card.eventContactId);
}

/** Mobile line for a contact id. NBAA26-000004 becomes #000004. */
export function shortContactId(card: DebriefCard): string | null {
  const full = contactIdLabel(card);
  if (!full) return null;
  const suffix = full.match(/(\d+)$/);
  return suffix ? `#${suffix[1]}` : full;
}

export function cardsForEvent(cards: DebriefCard[], eventKey: string): DebriefCard[] {
  return cards
    .filter((card) => card.eventKey === eventKey && card.eventContactId != null)
    .slice()
    .sort(compareEventContact);
}

export function nextEventContactId(cards: DebriefCard[], eventKey: string): number {
  const used = cards
    .filter((card) => card.eventKey === eventKey)
    .map((card) => card.eventContactId ?? 0);
  return Math.max(0, ...used) + 1;
}

export function progressCounts(cards: DebriefCard[]): { total: number; reviewed: number; unrated: number } {
  return {
    total: cards.length,
    reviewed: cards.filter((card) => card.reviewed).length,
    unrated: cards.filter((card) => card.rating === 0).length,
  };
}

export function formatPhone(e164: string | null): string {
  if (!e164) return "";
  const digits = e164.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return e164;
}

export function linkedInHref(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" || !LINKEDIN_HOST.test(parsed.hostname)) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function linkedInLabel(url: string): string {
  const parsed = new URL(url);
  const path = parsed.pathname.replace(/\/$/, "") || "/";
  return `LinkedIn: ${path}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "");
  return letters.join("") || "?";
}

export type CompanyNameSort = "asc" | "desc";

export function latestNote(card: DebriefCard): DebriefNote | null {
  return card.notes.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null;
}

export function latestFollowup(card: DebriefCard): DebriefFollowup | null {
  return card.followups.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null;
}

export function flagsKnown(card: DebriefCard): boolean {
  return typeof card.keyContact === "boolean" && typeof card.priorityFollowup === "boolean";
}

/** `null` keeps every card. An empty key keeps contacts with no owner. */
export function cardsMatchingOwner(
  cards: DebriefCard[],
  ownerKey: string | null,
  keyOf: (card: DebriefCard) => string = (card) => card.ownerUserId?.trim().toLowerCase() ?? "",
): DebriefCard[] {
  if (ownerKey == null) return cards;
  return cards.filter((card) => keyOf(card) === ownerKey);
}

/** Key contacts on priority accounts, unless show all is on. Show all is the full show roster. Lead rating 5 comes first, then company name. */
export function prioritizedCards(
  cards: DebriefCard[],
  query: string,
  eventKey: string,
  showAll: boolean,
  companySort: CompanyNameSort,
): DebriefCard[] {
  const matched = searchCards(cards, query, eventKey, false);
  const rows = showAll ? matched : matched.filter((card) => card.priorityFollowup === true && card.keyContact === true);
  return rows.sort((a, b) => {
    if (a.rating !== b.rating) return b.rating - a.rating;
    const company = a.accountName.localeCompare(b.accountName, undefined, { sensitivity: "base" });
    if (company !== 0) return companySort === "desc" ? -company : company;
    return a.fullName.localeCompare(b.fullName, undefined, { sensitivity: "base" });
  });
}

export function searchCards(cards: DebriefCard[], query: string, eventKey: string, showAll: boolean): DebriefCard[] {
  const needle = query.trim().toLowerCase();
  const scoped = showAll ? cards : cards.filter((card) => card.eventKey === eventKey);
  const matched = needle
    ? scoped.filter((card) =>
        [card.fullName, card.accountName, card.title, card.email ?? ""].join(" ").toLowerCase().includes(needle),
      )
    : scoped;
  return matched.slice().sort((a, b) => {
    const aOn = a.eventKey === eventKey ? 0 : 1;
    const bOn = b.eventKey === eventKey ? 0 : 1;
    if (aOn !== bOn) return aOn - bOn;
    if (aOn === 0) return compareEventContact(a, b);
    return a.fullName.localeCompare(b.fullName);
  });
}

export function wrapIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
}

export function dotWindow(index: number, total: number, size = 7): number[] {
  if (total <= 0) return [];
  if (total <= size) return Array.from({ length: total }, (_, i) => i);
  let start = index - Math.floor(size / 2);
  if (start < 0) start = 0;
  if (start > total - size) start = total - size;
  return Array.from({ length: size }, (_, i) => start + i);
}

export function isLeadRating(value: number): value is LeadRating {
  return value >= 0 && value <= 5 && Number.isInteger(value);
}

export function userIdsMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Add or remove one user. Matching ignores email case so a reloaded mark still toggles off. */
export function toggleUserId(ids: string[], userId: string): string[] {
  if (ids.some((id) => userIdsMatch(id, userId))) return ids.filter((id) => !userIdsMatch(id, userId));
  return [...ids, userId];
}

export function familiarNames(card: DebriefCard, currentUserId: string, names: Map<string, string>): string[] {
  return card.familiarUserIds
    .filter((userId) => !userIdsMatch(userId, currentUserId))
    .map((userId) => names.get(userId) ?? firstNameFromUserKey(userId));
}

export function addCardToEvent(cards: DebriefCard[], contactId: string, event: DebriefEvent): DebriefCard[] {
  const eventContactId = nextEventContactId(cards, event.eventKey);
  return cards.map((card) => {
    if (card.contactId !== contactId || card.eventKey === event.eventKey) return card;
    const attribution = card.attribution.includes(event.attribution)
      ? card.attribution
      : [...card.attribution, event.attribution];
    return {
      ...card,
      eventKey: event.eventKey,
      eventContactId,
      campaignKey: event.campaignKey,
      attribution,
      updatedAt: new Date().toISOString(),
    };
  });
}
